import { elementSizeCssClass, medium } from "../size/index.js";
import defineElementWithOneChild from "../helpers/defineElementWithOneChild.js";

/**
 * Element that can be used with or without a child.
 *
 * As a standalone, it will render a pill-looking badge, while when
 * a child is provided, it will render on top of it (in the top-right
 * corner) to display its content.
 *
 * Its content is computed, in order as:
 *   count -> label -> icon
 *
 * `corner` and `ribbon` parameters are mutually exclusive.
 *
 * Note that only one child at max is supported.
 *
 * @param {object} [options]
 * @param {boolean} [options.count] - Count value of the badge. We made a
 *   special case of numbers as then we can apply some arithmetic to it
 *   (like a max value)
 * @param {boolean} [options.label] - Label to show in the badge. This is
 *   only used if `count` is not provided
 * @param {boolean} [options.icon] - Icon to render in place of the badge
 *   content. This is only used if `count` and `label` are not provided
 * @param {boolean} [options.corner] - When true, the badge cover the whole
 *   corner of its child with a triangle
 * @param {boolean} [options.ribbon] - When true, the badge is rendered as a
 *   ribbon going over its child
 * @param {boolean} [options.max] - Maximum value for the count. If the count
 *   is greater than `max`, then the content is rendered as `${max}+`
 * @param {boolean} [options.showZero] - When true, the count shows even if its
 *   value is zero
 * @param {boolean} [options.cornerSize] - Change the size of the corner
 *   triangle, in case of a long label
 */

export default defineElementWithOneChild(
	"badge",
	(
		{
			count,
			label,
			icon,
			corner = false,
			ribbon = false,
			max = 99,
			showZero = false,
			cornerSize = medium,
		},
		children,
	) => ({
		tag: "span",
		options: {
			class: [
				{
					corner,
					text: !!label,
					ribbon,
					icon,
					count: count !== undefined,
					standalone: !children,
				},
				elementSizeCssClass("corner-size", cornerSize),
			],
		},
		children: (html) => {
			let content = label ?? icon;

			if (count !== undefined) {
				if (count === 0 && !showZero) {
					content = null;
				} else if (count > max) {
					content = `${max}+`;
				} else {
					content = count.toString();
				}
			}

			if (content) {
				html.span({ class: "content-wrapper" }, content);
			}

			if (children) {
				html.render(children);
			}
		},
	}),
);
